import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TEST_HOSPITAL_MODULE_CHOICES } from '@hospasense/contracts';
import { ApiError, apiSend } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  PageHeader,
  PermissionDeniedState,
  useToast,
} from '../components/ui/index.js';

/**
 * New test hospital — for a SenseSys colleague.
 *
 * Name, @sensesys.org email, tick the modules, press the button. The hospital
 * system builds a fresh evaluation hospital exactly as an approved signup
 * would (sample patients, staff, a login for every role), turns on only the
 * ticked modules, signs in as every login to prove it works, and only then
 * emails the colleague. Each colleague gets their own hospital, so nobody's
 * password ever changes under someone else.
 */
const DEFAULT_PICK = new Set(
  TEST_HOSPITAL_MODULE_CHOICES.filter((m) => m.defaultOn).map((m) => m.key as string),
);
const GROUPS = [...new Set(TEST_HOSPITAL_MODULE_CHOICES.map((m) => m.group))];
const EMAIL_OK = /^[^\s@]+@sensesys\.org$/i;

export function TestHospitalPage() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [hospitalNameRaw, setHospitalName] = useState('');
  const [picked, setPicked] = useState<Set<string>>(() => new Set(DEFAULT_PICK));

  const suggested = `${firstName.trim()} ${lastName.trim()} Evaluation Hospital`.trim();
  const hospitalName = nameTouched ? hospitalNameRaw : firstName || lastName ? suggested : '';

  const build = useMutation({
    mutationFn: () =>
      apiSend<{ organisationId: string }>('POST', '/api/v1/test-hospitals', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        testerEmail: email.trim(),
        hospitalName: hospitalName.trim(),
        modules: [...picked],
      }),
    onSuccess: ({ organisationId }) => {
      toast.success('Building it now. The logins are emailed once every one is checked.');
      void qc.invalidateQueries({ queryKey: ['organisations'] });
      navigate(`/organisations/${organisationId}`);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not start the test hospital.'),
  });

  const emailOk = EMAIL_OK.test(email.trim());
  const ready = firstName.trim() && lastName.trim() && emailOk && hospitalName.trim().length >= 3;
  const extras = useMemo(
    () => TEST_HOSPITAL_MODULE_CHOICES.filter((m) => !m.mandatory && picked.has(m.key)).length,
    [picked],
  );

  if (!hasPermission('organisation.create') || !hasPermission('provisioning.dispatch')) {
    return <PermissionDeniedState what="build test hospitals" />;
  }

  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-6">
      <PageHeader
        title="New test hospital"
        subtitle="A fresh hospital for one SenseSys colleague, with the modules you choose and a checked login for every role"
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: 'Control', to: '/' },
              { label: 'Organisations', to: '/organisations' },
              { label: 'New test hospital' },
            ]}
          />
        }
      />

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) build.mutate();
        }}
      >
        <Card>
          <CardHeader title="Who is it for?" />
          <CardBody className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <Input
              label="Their email"
              type="email"
              placeholder="name@sensesys.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              {...(email && !emailOk ? { error: 'Must be a @sensesys.org address' } : {})}
            />
            <Input
              label="Hospital name"
              value={hospitalName}
              onChange={(e) => {
                setNameTouched(true);
                setHospitalName(e.target.value);
              }}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Modules"
            subtitle={`The four core modules are always on · ${extras} more chosen`}
          />
          <CardBody className="space-y-5">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPicked(new Set(TEST_HOSPITAL_MODULE_CHOICES.map((m) => m.key)))}
              >
                Tick all
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPicked(new Set(DEFAULT_PICK))}
              >
                Usual set
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPicked(new Set())}
              >
                Core only
              </Button>
            </div>
            {GROUPS.map((group) => (
              <fieldset key={group}>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {group}
                </legend>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {TEST_HOSPITAL_MODULE_CHOICES.filter((m) => m.group === group).map((m) => (
                    <label key={m.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        data-testid={`module-${m.key}`}
                        checked={m.mandatory || picked.has(m.key)}
                        disabled={m.mandatory}
                        onChange={() => toggle(m.key)}
                      />
                      <span>
                        {m.label}
                        {m.mandatory && <span className="text-text-muted"> · always on</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
          </CardBody>
        </Card>

        <div className="flex items-center justify-end gap-3">
          <p className="text-sm text-text-muted">
            Takes about 5 minutes. You will see the result on the hospital’s page.
          </p>
          <Button type="submit" disabled={!ready} loading={build.isPending}>
            Build hospital and email logins
          </Button>
        </div>
      </form>
    </div>
  );
}
